import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { t, useLang } from "../lib/i18n";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { api, formatApiError } from "../lib/api";

export default function LoginPage() {
  useLang(); // re-render on language change
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resending, setResending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  // If user is already signed in, send them to Discover (or where they came from)
  useEffect(() => {
    if (user && typeof user === "object") {
      navigate(from, { replace: true });
    }
  }, [user, from, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNeedsVerify(false);
    setSubmitting(true);
    const result = await login(email, password);
    setSubmitting(false);
    if (result.ok) {
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } else {
      setError(result.error);
      // Backend blocks unverified logins with a message that includes
      // "verified" / "verify" — expose a Resend button in that case. The
      // login endpoint already auto-sends a fresh link, but a dedicated
      // button gives the user a clear way to re-trigger it themselves.
      const msg = (result.error || "").toLowerCase();
      if (msg.includes("verif")) setNeedsVerify(true);
    }
  };

  const onResend = async () => {
    if (!email || !password) return;
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email, password });
      toast.success(t("auth.resend_sent"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-stretch fade-up pb-28 sm:pb-8">
      <div className="hidden md:block w-1/2 bg-[#E8DFC1] relative overflow-hidden border-r border-[rgba(0,0,0,0.08)]">
        <div className="absolute inset-0 p-16 flex flex-col justify-between">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252]">reads · 2026</div>
            <h2 className="font-heading text-4xl lg:text-5xl text-[#0A0A0A] mt-8 leading-[0.98] tracking-tight">
              {t("auth.your_journal")}
            </h2>
            <p className="text-[#525252] mt-4 max-w-sm">{t("auth.pick_up")}</p>
          </div>
          <div className="grid grid-cols-3 gap-4 font-mono text-[10px] uppercase tracking-widest text-[#525252]">
            <div><div className="text-[#0A0A0A] text-2xl font-heading mb-1">01</div>Rate</div>
            <div><div className="text-[#0A0A0A] text-2xl font-heading mb-1">02</div>Review</div>
            <div><div className="text-[#0A0A0A] text-2xl font-heading mb-1">03</div>Discover</div>
          </div>
        </div>
      </div>
      <div className="w-full md:w-1/2 flex items-center justify-center p-8 md:p-16">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252]">{t("auth.welcome_back")}</div>
            <h1 className="font-heading text-4xl text-[#0A0A0A] mt-3">{t("nav.signin")}</h1>
          </div>

          <div className="space-y-1.5">
            <label className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{t("register.email")}</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#FAF5E6] border border-[rgba(0,0,0,0.08)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
              data-testid="login-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{t("register.password")}</label>
              <Link to="/forgot-password" className="font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A]" data-testid="login-forgot-link">{t("auth.forgot")}</Link>
            </div>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#FAF5E6] border border-[rgba(0,0,0,0.08)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
              data-testid="login-password-input"
            />
          </div>

          {error && (
            <div className="text-sm text-[#B91C1C] border border-[#B91C1C]/20 bg-[#B91C1C]/5 p-3 rounded-sm space-y-2" data-testid="login-error">
              <div>{error}</div>
              {needsVerify && (
                <button
                  type="button"
                  onClick={onResend}
                  disabled={resending || !email || !password}
                  className="font-mono uppercase tracking-widest text-[10px] text-[#0A0A0A] underline underline-offset-4 disabled:opacity-50"
                  data-testid="login-resend-verification-button"
                >
                  {resending ? t("auth.resending") : t("auth.resend_verification")}
                </button>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#0A0A0A] hover:bg-[#262626] disabled:opacity-60 text-white font-mono uppercase tracking-widest text-xs py-3.5 rounded-sm transition-colors"
            data-testid="login-submit-button"
          >
            {submitting ? t("auth.signing_in") : t("nav.signin")}
          </button>

          <div className="flex items-center gap-3 my-2">
            <div className="flex-1 h-px bg-[rgba(10,10,10,0.10)]" />
            <span className="font-mono uppercase tracking-widest text-[10px] text-[#8C7F65]">{t("auth.or")}</span>
            <div className="flex-1 h-px bg-[rgba(10,10,10,0.10)]" />
          </div>

          <GoogleSignInButton label={t("auth.continue_google")} testId="login-google" />

          <div className="text-sm text-[#525252] text-center">
            {t("auth.new_here")} <Link to="/register" className="text-[#0A0A0A] underline underline-offset-4" data-testid="login-to-register">{t("auth.create_account_link")}</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
